'use client';
import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';

// Last-resort boundary for errors thrown in the root layout/template that the
// per-route error boundary cannot catch. It replaces the whole document, so it
// runs without app providers (no i18n) — copy is intentionally inline and plain.
export default function GlobalError({
    error,
    reset,
}: {
    error: Error & { digest?: string };
    reset: () => void;
}) {
    useEffect(() => {
        Sentry.captureException(error);
    }, [error]);

    return (
        <html lang="en">
            <body
                style={{
                    margin: 0,
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px',
                    padding: '32px 24px',
                    textAlign: 'center',
                    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
                    color: '#111',
                }}
            >
                <h1 style={{ fontSize: '20px', fontWeight: 600, margin: 0 }}>Something went wrong</h1>
                <p
                    style={{
                        fontSize: '15px',
                        lineHeight: 1.5,
                        color: 'rgba(0,0,0,0.6)',
                        margin: 0,
                        maxWidth: '420px',
                    }}
                >
                    An error occurred while loading the page. Please try again.
                </p>
                <div
                    style={{
                        display: 'flex',
                        gap: '12px',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        marginTop: '8px',
                    }}
                >
                    <button
                        type="button"
                        onClick={reset}
                        style={{
                            appearance: 'none',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '10px 20px',
                            fontSize: '15px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            background: '#111',
                            color: '#fff',
                        }}
                    >
                        Try again
                    </button>
                    {/* This boundary replaces the whole document and runs outside the
                        app/router, so next/link is unavailable — a plain anchor is correct here. */}
                    {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
                    <a
                        href="/"
                        style={{
                            borderRadius: '8px',
                            padding: '10px 20px',
                            fontSize: '15px',
                            fontWeight: 500,
                            background: 'transparent',
                            color: '#111',
                            border: '1px solid rgba(0,0,0,0.15)',
                            textDecoration: 'none',
                        }}
                    >
                        Back to home
                    </a>
                </div>
            </body>
        </html>
    );
}
